import { Checkbox } from "@/components/ui/checkbox";
import { useState } from "react";
import type { ContactMessage } from "../../types/contact.types";
import { Button } from "@/components/ui/button";
import { formatActivityTimestamp } from "@/lib/dateUtils";

interface RequestListRowProps {
  request: ContactMessage;
  onToggleReadStatus: (messageId: string, currentStatus: boolean) => void;
  isUpdating?: boolean;
  isSelected: boolean;
  onSelect: (messageId: string) => void;
  onClick: (message: ContactMessage) => void;
  isActive?: boolean;
  onDelete?: (messageId: string) => void;
}

export function RequestListRow({
  request,
  onToggleReadStatus,
  // isUpdating,
  isSelected,
  onSelect,
  onClick,
  isActive,
  onDelete,
}: RequestListRowProps) {
  const [isHovered, setIsHovered] = useState(false);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(request._id);
  };

  const handleRowClick = () => {
    onClick(request);
    // Mark as read when clicked
    if (!request.is_read) {
      onToggleReadStatus(request._id, false);
    }
  };

  const handleToggleRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleReadStatus(request._id, request.is_read);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(request._id);
    }
  };

  return (
    <div
      onClick={handleRowClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`group hover:bg-dark-surface flex h-[52px] cursor-pointer flex-wrap items-center gap-4 px-5 py-3 transition-colors ${
        isActive
          ? "bg-dark-surface shadow-[0_0_8px_#FFFFFF40]"
          : request.is_read
            ? "bg-admin-bg transition-all duration-150 hover:shadow-[0_0_8px_#FFFFFF40]"
            : "bg-black-950 transition-all duration-150 hover:shadow-[0px_0px_8px_0px_#FFFFFF40]"
      }`}
    >
      {/* Checkbox */}
      <div onClick={handleCheckboxClick} className="flex-shrink-0">
        <Checkbox
          checked={isSelected}
          className={`h-5 w-5 border-white ${
            isSelected
              ? "border-orange-500 bg-orange-500"
              : "bg-transparent hover:border-gray-400"
          }`}
        />
      </div>

      {/* Name */}
      <div className="w-40 flex-shrink-0">
        <h3
          className={`text-md truncate leading-6 -tracking-wide text-white ${
            request.is_read ? "font-medium" : "font-bold"
          }`}
        >
          {request.name}
        </h3>
      </div>

      {/* Company and Message */}
      <div className="min-w-0 flex-1 transition-all duration-150">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={`text-md flex-shrink-0 leading-6 -tracking-wide text-white ${
              request.is_read ? "font-medium" : "font-bold"
            }`}
          >
            {request.company}
          </span>
          <span className="text-black-400 flex-shrink-0">-</span>
          <p className="text-black-400 text-md min-w-0 flex-1 truncate leading-6 -tracking-wide">
            {request.message}
          </p>
        </div>
      </div>

      {/* Date / Action Icons - Fixed width to prevent layout shift */}
      {/* Date / Action Icons - Fixed width to prevent layout shift */}
      <div className="relative flex h-full w-[180px] items-center justify-end">
        {/* Always render both, toggle visibility */}
        <div
          className={`absolute right-0 flex items-center gap-3 transition-opacity ${isHovered ? "opacity-100" : "pointer-events-none opacity-0"}`}
        >
          {/* Delete */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDelete}
            className="rounded p-1.5 text-white transition-colors hover:bg-white/10 hover:text-red-400"
            title="Delete"
          >
            <i className="ri-delete-bin-line text-md"></i>
          </Button>

          {/* Toggle Read/Unread */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleToggleRead}
            className="rounded p-1.5 text-white transition-colors hover:bg-white/10 hover:text-white"
            title={request.is_read ? "Mark as unread" : "Mark as read"}
          >
            {request.is_read ? (
              <i className="ri-mail-unread-line text-md"></i>
            ) : (
              <i className="ri-mail-open-line text-md"></i>
            )}
          </Button>
        </div>

        <p
          className={`text-black-400 text-md text-right leading-6 -tracking-wide transition-opacity ${isHovered ? "pointer-events-none opacity-0" : "opacity-100"}`}
        >
          {formatActivityTimestamp(request.created_at)}
        </p>
      </div>
    </div>
  );
}
